package com.devi3east.bashbuddy.leaderboard.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

@Composable
fun LeaderboardRules(
    date: String
) {
    val scrollState = rememberScrollState()

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(scrollState),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = 8.dp),
            shape = RoundedCornerShape(16.dp),
            color = MaterialTheme.colorScheme.primaryContainer,
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.primary)
        ) {
            Column(
                modifier = Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(
                    text = "Win a $500 Donation for Your Organization!",
                    style = MaterialTheme.typography.titleMedium.copy(
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp
                    ),
                    color = MaterialTheme.colorScheme.onPrimaryContainer,
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = TextAlign.Center
                )

                val keySteps = listOf(
                    "Create your organization account",
                    "Post an eligible upcoming event",
                    "Complete Stripe onboarding",
                    "Encourage your community to register",
                    "Ensure users select your organization during sign-up",
                    "The organization with the most registered users wins"
                )

                Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    keySteps.forEach { step ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(8.dp)
                                    .background(
                                        color = MaterialTheme.colorScheme.primary,
                                        shape = RoundedCornerShape(50)
                                    )
                            )
                            Spacer(modifier = Modifier.width(12.dp))
                            Text(
                                text = step,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onPrimaryContainer,
                                modifier = Modifier.weight(1f)
                            )
                        }
                    }
                }
            }
        }

        Text(
            text = "Leaderboard Rules",
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold
        )

        Text(
            text = "Last updated: $date",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )

        Text(
            text = "Promotion Summary",
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
        )

        val promotionSummary = listOf(
            "Organizations must create an account, complete Stripe onboarding, and post at least one eligible upcoming event to qualify.",
            "Stripe onboarding verifies organization legitimacy and can be completed by navigating to Organization -> Stripe within the app.",
            "Once onboarding and event posting are complete, the organization becomes eligible to compete for a $500 donation provided by bash buddy.",
            "The promotion period ends at 11:59 PM ET on September 4, 2026. The winner will be announced on September 5, 2026.",
            "Eligible events must take place after May 1, 2026. Past events are not allowed."
        )

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            promotionSummary.forEach { ClassicBulletRow(it) }
        }

        Text(
            text = "Organization Eligibility",
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
        )

        val organizationEligibility = listOf(
            "Only legitimate organizations are eligible to participate. Individual persons, personal projects, or informal groups are not eligible.",
            "Each organization may only participate once and may maintain only one organization account.",
            "bash buddy reserves the right to approve, reject, or remove organizations that do not meet eligibility requirements."
        )

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            organizationEligibility.forEach { ClassicBulletRow(it) }
        }

        Text(
            text = "Eligible Events",
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
        )

        val eligibleEvents = listOf(
            "Events must be public-facing fundraising or ticketed events intended to support the organization or a cause the organization actively supports.",
            "Events must be open to the public, either in-person or virtual, and marketed beyond internal organization members.",
            "Events must include a clear title, description, date, and location or virtual platform.",
            "Events may not contain placeholder content such as 'test', 'TBD', or similar language.",
            "Events must comply with all applicable laws and platform policies."
        )

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            eligibleEvents.forEach { ClassicBulletRow(it) }
        }

        val ineligibleEvents = listOf(
            "Internal meetings, planning sessions, rehearsals, or private gatherings.",
            "Test events, placeholder events, or events created solely to gain leaderboard eligibility.",
            "Duplicate or substantially similar events intended to manipulate eligibility.",
            "Events with no legitimate fundraising, ticketing, or public-facing purpose."
        )

        Text(
            text = "Ineligible Events Include (but are not limited to):",
            style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.SemiBold)
        )

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            ineligibleEvents.forEach { ClassicBulletRow(it) }
        }

        Text(
            text = "Voting Rules",
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
        )

        val votingRules = listOf(
            "Users may vote by selecting an affiliated organization either during account sign-up or later from the Profile screen.",
            "Selecting an organization during sign-up is strongly recommended, as all selections are subject to verification to prevent fraudulent or duplicate votes.",
            "Users who select an organization after sign-up will go through the same verification process as those who select during registration.",
            "Each verified user account counts as one vote for the selected organization.",
            "Once an organization is selected, the choice is locked and cannot be changed.",
            "Users may not create multiple accounts. Any attempt to manipulate the voting process, including duplicate accounts, may result in disqualification.",
            "Users have until 11:59 PM EST on September 4, 2026 to select their organization and have their vote counted."
        )

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            votingRules.forEach { ClassicBulletRow(it) }
        }

        Text(
            text = "Winner Selection",
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
        )

        val winnerSelection = listOf(
            "The organization with the highest number of verified affiliated users at the end of the promotion will receive the $500 donation.",
            "Voting closes at 11:59 PM EST on September 4, 2026.",
            "Leaderboard standings displayed in the app are for informational purposes only and are not guaranteed to reflect real-time or final verified vote totals.",
            "Because votes are subject to verification, the leaderboard at the time voting closes may not represent the final outcome.",
            "Final results will be reviewed, verified, and officially declared after the promotion ends.",
            "In the event no clear winner exists after verification, the promotion may be extended in 24-hour increments until a winner is determined.",
            "All decisions regarding eligibility, vote verification, leaderboard calculations, and winner selection are final and made at the sole discretion of bash buddy."
        )

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            winnerSelection.forEach { ClassicBulletRow(it) }
        }

        Text(
            text = "Prize Distribution & Taxes",
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
        )

        val prizeDistribution = listOf(
            "The $500 prize will be issued as a donation to the winning organization.",
            "Prize delivery method may include check by mail or another method determined by bash buddy.",
            "The winning organization is solely responsible for any applicable taxes, reporting obligations, or fees associated with receiving the donation."
        )

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            prizeDistribution.forEach { ClassicBulletRow(it) }
        }

        Text(
            text = "Disqualification & Enforcement",
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
        )

        val enforcementRules = listOf(
            "bash buddy reserves the right to remove or disqualify any organization or event determined to be misleading, ineligible, or fraudulent.",
            "Disqualification may occur if an organization creates fake or misleading events, manipulates sign-ups, or violates platform policies.",
            "bash buddy may request additional information from organizations if eligibility is questioned.",
            "bash buddy does not guarantee verification of all events, but retains full authority to enforce contest rules."
        )

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            enforcementRules.forEach { ClassicBulletRow(it) }
        }

        // --- Responsibilities & Final Notes ---
        Text(
            text = "Responsibilities & Final Notes",
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
        )

        val responsibilities = listOf(
            "Organizations are responsible for ensuring compliance with all contest rules.",
            "Only properly created user accounts and eligible votes will be counted.",
            "bash buddy reserves the right to modify, suspend, or terminate the promotion at any time for any reason.",
            "Participation in the promotion constitutes acceptance of all rules and decisions made by bash buddy."
        )

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            responsibilities.forEach { ClassicBulletRow(it) }
        }
    }
}